import { Link, useNavigate } from 'react-router-dom';
import { Search, Disc3, Lock, LogIn, LogOut, Heart } from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import { usePlayer } from '../context/PlayerContext';
import { useState, useRef, useEffect } from 'react';
import NowPlayingTicker from './NowPlayingTicker';
import LiveListenerBadge from './LiveListenerBadge';

const Header = () => {
  const navigate = useNavigate();
  const { user, loading, login, logout } = useAuth();
  const { currentSong, isExpanded } = usePlayer();
  const [menuOpen, setMenuOpen] = useState(false);
  const menuRef = useRef(null);

  useEffect(() => {
    if (!menuOpen) return;
    const onDoc = (e) => {
      if (menuRef.current && !menuRef.current.contains(e.target)) setMenuOpen(false);
    };
    document.addEventListener('mousedown', onDoc);
    return () => document.removeEventListener('mousedown', onDoc);
  }, [menuOpen]);

  // Push header down so the top mini-player sits above it
  const headerTop = currentSong && !isExpanded ? 68 : 0;

  return (
    <header
      data-testid="app-header"
      className="sticky z-30 backdrop-blur-xl bg-[#120505]/80 border-b border-[#3D1A1A]"
      style={{ top: `${headerTop}px` }}
    >
      <div className="max-w-5xl mx-auto flex items-center gap-3 px-4 py-3">
        <Link to="/" data-testid="home-link" className="flex items-center gap-2 group">
          <span className="w-9 h-9 rounded-full bg-gradient-to-br from-[#FFB800] to-[#FF007F] flex items-center justify-center shadow-[0_0_20px_rgba(255,0,127,0.4)]">
            <Disc3 className="w-5 h-5 text-[#120505] group-hover:vinyl-spin" />
          </span>
          <div className="leading-tight">
            <div className="font-display text-lg text-[#FFF3E0] tracking-tight">Patna Ke Presleys</div>
            <div className="text-[10px] uppercase tracking-[0.2em] text-[#FFB800]/80 hidden sm:block">Bihar ka beat, Bollywood ka jazba</div>
          </div>
        </Link>
        <div className="flex-1" />
        <div className="hidden sm:block">
          <LiveListenerBadge />
        </div>
        <button
          data-testid="search-btn"
          onClick={() => navigate('/search')}
          aria-label="Search"
          className="w-10 h-10 rounded-full flex items-center justify-center text-[#FFF3E0] hover:bg-[#220A0A] transition-colors"
        >
          <Search className="w-5 h-5" />
        </button>

        {/* Auth control */}
        {loading ? (
          <div className="w-10 h-10 rounded-full bg-[#220A0A] animate-pulse" />
        ) : user ? (
          <div className="relative" ref={menuRef}>
            <button
              data-testid="user-menu-btn"
              onClick={() => setMenuOpen((o) => !o)}
              className="w-10 h-10 rounded-full overflow-hidden border border-[#3D1A1A] hover:border-[#FFB800] transition-colors"
              aria-label="Account menu"
            >
              {user.picture ? (
                <img src={user.picture} alt={user.name} className="w-full h-full object-cover" />
              ) : (
                <div className="w-full h-full flex items-center justify-center bg-[#FFB800] text-[#120505] font-bold text-sm">
                  {user.name?.[0]?.toUpperCase() || 'U'}
                </div>
              )}
            </button>
            {menuOpen && (
              <div
                data-testid="user-menu"
                className="absolute right-0 mt-2 w-56 rounded-xl bg-[#120505]/95 backdrop-blur-xl border border-[#3D1A1A] shadow-[0_10px_40px_rgba(0,0,0,0.5)] overflow-hidden slide-up"
              >
                <div className="px-4 py-3 border-b border-[#3D1A1A]">
                  <div className="text-sm text-[#FFF3E0] truncate">{user.name}</div>
                  <div className="text-xs text-[#BFA5A5] truncate">{user.email}</div>
                </div>
                <Link
                  to="/meri-pasand"
                  data-testid="menu-meri-pasand-link"
                  onClick={() => setMenuOpen(false)}
                  className="w-full flex items-center gap-2 px-4 py-3 text-sm text-[#FFF3E0] hover:bg-[#220A0A] transition-colors border-b border-[#3D1A1A]"
                >
                  <Heart className="w-4 h-4 text-[#FF007F]" />
                  Meri Pasand
                </Link>
                <button
                  data-testid="logout-btn"
                  onClick={() => { setMenuOpen(false); logout(); }}
                  className="w-full flex items-center gap-2 px-4 py-3 text-sm text-[#FFF3E0] hover:bg-[#220A0A] transition-colors"
                >
                  <LogOut className="w-4 h-4 text-[#FF007F]" />
                  Sign out
                </button>
              </div>
            )}
          </div>
        ) : (
          <button
            data-testid="login-btn"
            onClick={login}
            className="h-10 px-4 rounded-full bg-[#FFB800] text-[#120505] font-bold text-sm flex items-center gap-1.5 hover:scale-[1.03] active:scale-[0.98] transition-transform shadow-[0_4px_16px_rgba(255,184,0,0.3)]"
          >
            <LogIn className="w-4 h-4" />
            <span className="hidden sm:inline">Sign in</span>
          </button>
        )}

        <Link
          to="/admin"
          data-testid="admin-link"
          aria-label="Admin"
          className="w-10 h-10 rounded-full flex items-center justify-center text-[#BFA5A5] hover:text-[#FFB800] hover:bg-[#220A0A] transition-colors"
        >
          <Lock className="w-4 h-4" />
        </Link>
      </div>
      <NowPlayingTicker />
    </header>
  );
};

export default Header;
